'use client'

/**
 * Client hook that supplies published translation overrides to content rendered
 * from hard-coded per-locale tables (docs + learn). Server surfaces fetch these
 * via `i18n/overrides.ts#fetchOverrides`; client pages can't use that tagged
 * server fetch, so this hook fetches the public `/i18n/overrides/:locale`
 * endpoint directly, with a module-level cache (mirroring `useLocaleSettings`).
 *
 * Gated on `NEXT_PUBLIC_I18N_OVERRIDES_ENABLED` (the same flag request.ts uses):
 * returns {} in production so the page renders the bundled table values, never
 * an extra fetch. The public endpoint already filters to publicly-enabled
 * locales, so a disabled-locale preview falls back to the table here — and is
 * instead previewed live via the `/translate` bridge's DOM overlay.
 */

import { useEffect, useState } from 'react'
import { getApiUrl } from '@/lib/serverConfig'
import { contentOverridesEnabled } from '@/lib/i18n/contentOverrides'

const TTL_MS = 60_000
const EMPTY: Record<string, string> = {}

type Cached = { value: Record<string, string>; fetchedAt: number }
const cacheByLocale = new Map<string, Cached>()
const inflightByLocale = new Map<string, Promise<Record<string, string>>>()

async function fetchPublishedOverrides(locale: string): Promise<Record<string, string>> {
  const cached = cacheByLocale.get(locale)
  if (cached && Date.now() - cached.fetchedAt < TTL_MS) return cached.value
  const existing = inflightByLocale.get(locale)
  if (existing) return existing

  const inflight = (async () => {
    try {
      const res = await fetch(`${getApiUrl()}/i18n/overrides/${locale}`)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = (await res.json()) as { overrides?: Record<string, string> }
      const value = data.overrides ?? EMPTY
      cacheByLocale.set(locale, { value, fetchedAt: Date.now() })
      return value
    } catch {
      // Soft-fail to the last known value (or empty); never break the page.
      const value = cacheByLocale.get(locale)?.value ?? EMPTY
      cacheByLocale.set(locale, { value, fetchedAt: Date.now() })
      return value
    } finally {
      inflightByLocale.delete(locale)
    }
  })()

  inflightByLocale.set(locale, inflight)
  return inflight
}

export function usePublishedOverrides(locale: string): Record<string, string> {
  const [state, setState] = useState<{ locale: string; value: Record<string, string> }>(() => ({
    locale,
    value: cacheByLocale.get(locale)?.value ?? EMPTY,
  }))

  // Locale changed since the last commit: synchronously adopt the new locale's
  // cached map (or EMPTY) so we never apply the previous locale's overrides to
  // the new locale's table copy while the fetch below is still in flight.
  // setState-during-render is React's supported pattern for deriving state from
  // a changed prop (no extra commit, no effect, so no cascading-render lint).
  if (state.locale !== locale) {
    setState({ locale, value: cacheByLocale.get(locale)?.value ?? EMPTY })
  }

  useEffect(() => {
    // Disabled (production): never fetch. Initial state is already EMPTY because
    // the cache is only ever populated by the fetch below, so there's nothing to
    // reset — returning here keeps the page on its bundled table values.
    if (!contentOverridesEnabled()) return
    let cancelled = false
    void fetchPublishedOverrides(locale).then(next => {
      if (!cancelled) setState({ locale, value: next })
    })
    return () => {
      cancelled = true
    }
  }, [locale])

  // During the render where `locale` just changed, `state` hasn't caught up yet
  // (the setState above re-renders immediately after) — return the new locale's
  // cached value rather than the stale committed state.
  return state.locale === locale ? state.value : (cacheByLocale.get(locale)?.value ?? EMPTY)
}
